import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useStoreSettings } from "../context/StoreSettingsContext";
import { useAuth } from "../context/AuthContext";
import { scrollToId } from "../lib/scroll";

const links = [
  { label: "Offerte", id: "offerte" },
  { label: "Categorie", id: "categorie" },
  { label: "Box Mensile", id: "box-mensile" },
  { label: "Chi Siamo", id: "manifesto" },
  { label: "Ricorrenti", id: "ricorrenti" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { canShop } = useStoreSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const goHome = () => {
    navigate("/");
    setTimeout(() => scrollToId("top", 0), 60);
  };

  const goSection = (id) => {
    setMobileOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="safe-pt fixed top-0 inset-x-0 z-50"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-5">
        {/* Top row: brand (left) + account/cart (right) */}
        <div className="flex items-center justify-between">
          <button
            onClick={goHome}
            data-testid="logo-button"
            aria-label="Torna alla home"
            className="group flex items-baseline gap-1 leading-none"
          >
            <span className="notranslate font-serif text-4xl font-medium tracking-tight text-ink transition-colors group-hover:text-sage md:text-6xl" translate="no">
              NEED
            </span>
            <span className="h-2 w-2 rounded-full bg-sage md:h-2.5 md:w-2.5" />
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="burger-button"
              aria-label="Menu"
              className="grid h-10 w-10 place-items-center rounded-full border border-ink/15 bg-cream/70 text-ink backdrop-blur-xl md:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.6} /> : <Menu className="h-5 w-5" strokeWidth={1.6} />}
            </button>
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                data-testid="header-admin-button"
                className="hidden rounded-full border border-ink/15 bg-cream/70 px-4 py-2.5 text-sm font-medium text-ink backdrop-blur-xl transition-colors hover:bg-ink hover:text-cream sm:block"
              >
                Admin
              </button>
            )}
            <button
              onClick={() => navigate(user ? "/account" : "/login")}
              data-testid="header-account-button"
              className="flex items-center gap-2 rounded-full border border-ink/15 bg-cream/70 px-4 py-2.5 text-sm font-medium text-ink backdrop-blur-xl transition-colors hover:bg-ink hover:text-cream"
            >
              <User className="h-4 w-4" strokeWidth={1.6} />
              <span className="hidden sm:inline">{user ? (user.name?.split(" ")[0] || "Account") : "Accedi"}</span>
            </button>
            {canShop && (
              <button
                onClick={() => setOpen(true)}
                data-testid="open-cart-button"
                className="relative flex items-center gap-2 rounded-full bg-ink px-4 md:px-5 py-2.5 text-sm font-medium text-cream transition-transform hover:scale-[1.04] active:scale-95"
              >
                <ShoppingBag className="h-4 w-4" strokeWidth={1.6} />
                <span className="hidden sm:inline">Carrello</span>
                {count > 0 && (
                  <span data-testid="cart-count" className="ml-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-electric px-1 text-[11px] font-semibold text-cream">
                    {count}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Second row: centered navigation bar (desktop) */}
        <div className="mt-4 hidden justify-center md:flex">
          <nav
            data-testid="main-nav"
            className="flex max-w-full items-center gap-5 overflow-x-auto rounded-full border border-ink/10 bg-cream/70 px-5 py-2.5 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:gap-8 md:px-8"
          >
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollToId(l.id)}
                data-testid={`nav-${l.id}`}
                className="group relative whitespace-nowrap text-sm font-medium text-ink-soft transition-colors hover:text-ink"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-electric transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
        </div>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              data-testid="mobile-menu"
              className="mt-3 flex flex-col gap-1 rounded-2xl border border-ink/10 bg-cream/90 p-3 backdrop-blur-xl md:hidden"
            >
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goSection(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-ink transition-colors hover:bg-ink/10"
                >
                  {l.label}
                </button>
              ))}
              <div className="my-1 h-px bg-ink/10" />
              {user?.role === "admin" && (
                <button
                  onClick={() => { setMobileOpen(false); navigate("/admin"); }}
                  data-testid="mobile-admin"
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-ink transition-colors hover:bg-ink/10"
                >
                  Admin
                </button>
              )}
              <button
                onClick={() => { setMobileOpen(false); navigate(user ? "/account" : "/login"); }}
                data-testid="mobile-account"
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-base font-medium text-ink transition-colors hover:bg-ink/10"
              >
                <User className="h-4 w-4" strokeWidth={1.6} /> {user ? "Il mio account" : "Accedi"}
              </button>
              <button
                onClick={() => { setMobileOpen(false); setOpen(true); }}
                data-testid="mobile-cart"
                className="flex items-center gap-2 rounded-xl bg-sage px-4 py-3 text-left text-base font-semibold text-cream"
              >
                <ShoppingBag className="h-4 w-4" strokeWidth={1.6} /> Carrello{count > 0 ? ` (${count})` : ""}
              </button>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
